import { useRef, useState } from 'react';
import { commandIsUncertain, mailError } from '../mailboxModel';
import { attachmentLimitError, sendValidationError } from '../models/attachmentModel';
import { sendDraft, reconcileOutbound, fetchOutboundCommands } from '../models/mailApi';

export function useMailSend({ draft, setDraft, clearDraft, persistCommand, refresh, needsSignature, notify }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const busy = useRef(false);
  const locked = ['sending', 'uncertain', 'succeeded'].includes(draft.command?.status);
  const patch = values => setDraft(current => ({ ...current, ...values, command: null, delivery_key: crypto.randomUUID() }));
  const send = async () => {
    if (busy.current || locked || needsSignature) return;
    const validation = sendValidationError(draft); if (validation) { setError(validation); return; }
    busy.current = true; setWorking(true); setError('');
    const key = draft.delivery_key || crypto.randomUUID();
    const pending = { ...draft, command: { key, status: 'sending' } };
    try {
      // Journal identity before delivery; a reload must never offer a fresh send.
      persistCommand(pending);
    } catch (_) { setError('Save failed. Free browser storage before sending.'); busy.current = false; setWorking(false); return; }
    try {
      await sendDraft(draft, key);
      const completed = { ...draft, command: { key, status: 'succeeded' } };
      setDraft(completed);
      try { persistCommand(completed); } catch (_) { /* Existing sending journal remains safe. */ }
      clearDraft(); refresh(); notify.success('Email sent.');
    } catch (failure) {
      const next = { ...draft, command: { key, status: commandIsUncertain(failure) ? 'uncertain' : 'failed' } };
      setDraft(next); try { persistCommand(next); } catch (_) { /* Keep the prior journal. */ }
      setError(mailError(failure));
    } finally { busy.current = false; setWorking(false); }
  };
  const checkStatus = async () => {
    if (busy.current) return;
    busy.current = true; setWorking(true); setError('');
    try {
      await reconcileOutbound();
      const rows = await fetchOutboundCommands('succeeded');
      if (rows.some(row => row.idempotency_key === draft.command.key)) { clearDraft(); refresh(); notify.success('Delivery confirmed.'); }
      else setError('Delivery is still unconfirmed. Check Sent in Gmail before taking further action. This message has not been sent again.');
    } catch (failure) { setError(mailError(failure)); }
    finally { busy.current = false; setWorking(false); }
  };
  const attach = async event => {
    const files = Array.from(event.target.files || []); event.target.value = '';
    if (busy.current) return;
    const limitError = attachmentLimitError(draft.attachments, files); if (limitError) { setError(limitError); return; }
    busy.current = true; setWorking(true);
    try {
      const added = await Promise.all(files.map(file => new Promise((resolve, reject) => { const reader = new FileReader(); reader.onerror = () => reject(new Error('Unable to read attachment.')); reader.onload = () => resolve({ filename: file.name, mime_type: file.type || 'application/octet-stream', size_bytes: file.size, content_base64: String(reader.result).split(',')[1] }); reader.readAsDataURL(file); })));
      patch({ attachments: [...draft.attachments, ...added] });
    } catch (failure) { setError(mailError(failure)); }
    finally { busy.current = false; setWorking(false); }
  };
  return { working, error, locked, patch, send, checkStatus, attach };
}
